import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'

import { gsap, isCompactViewport, prefersReducedMotion, registerGsap } from '@/lib/gsap'

/**
 * Recreates Webflow's "Dashboard Animation" (a-67), a SCROLLING_IN_VIEW scrub
 * attached to `.dashboard-holder`:
 *   - "Manila"    (top text)   slides right  : xPercent -35 -> -9 over the first half
 *   - "Dashboard" (bottom text) slides left  : xPercent  80 -> -30 across the full range
 *   - the dashboard image grows in           : scale 0 -> 1 over the first half
 * Each text keeps its base vertical offset (yPercent -50 / +50) while x scrubs.
 */
export const DashboardSection: FC = (): ReactNode => {
    const holderRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        registerGsap()
        const holder = holderRef.current
        if (!holder) return
        if (prefersReducedMotion() || isCompactViewport()) return

        const ctx = gsap.context(() => {
            const tl = gsap.timeline({
                defaults: { ease: 'power1.out' },
                scrollTrigger: {
                    trigger: holder,
                    start: 'top bottom',
                    end: 'bottom top',
                    // Smoothing: GSAP `scrub` is the catch-up time in seconds, so a
                    // larger value = smoother, more trailing motion. ~85% smoothing
                    // to match the original Webflow SCROLLING_IN_VIEW feel.
                    scrub: 1.8
                }
            })

            // Pin x/y (px) to 0 so GSAP doesn't compound the base CSS
            // `translate(0, ±50%)` (read from computed style as px) with our
            // percentage offsets. Only xPercent/yPercent drive the position.
            tl.fromTo(
                '.dashboard-text-holder:not(.bottom)',
                { x: 0, y: 0, xPercent: -35, yPercent: -50 },
                { x: 0, y: 0, xPercent: -9, yPercent: -50, duration: 0.5 },
                0
            )
                .fromTo(
                    '.dashboard-image-holder',
                    { scale: 0 },
                    { scale: 1, duration: 0.5 },
                    0
                )
                .fromTo(
                    '.dashboard-text-holder.bottom',
                    { x: 0, y: 0, xPercent: 80, yPercent: 50 },
                    { x: 0, y: 0, xPercent: -30, yPercent: 50, duration: 1 },
                    0
                )
        }, holder)

        return () => ctx.revert()
    }, [])

    return (
        <div id='product' className='section overflow-hidden'>
            <div className='container container-big-text'>
                <div className='dashboard-wrapper'>
                    <div className='dashboard-holder' ref={holderRef}>
                        <div className='dashboard-text-holder'>
                            <div className='dashboard-text'>Manila</div>
                        </div>
                        <div className='dashboard-image-holder'>
                            <img
                                src='/images/Dashboard-Image.webp'
                                loading='lazy'
                                width={2012}
                                height={1303}
                                alt='Manila analytics dashboard'
                                className='dashboard-image'
                            />
                        </div>
                        <div className='dashboard-text-holder bottom'>
                            <div className='dashboard-text'>Dashboard</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}
